import { useAuth } from '@agentnet/auth';
import { DataTable } from '@fluentsms/agentnet-web-components';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { ColDef, FilterChangedEvent, FirstDataRenderedEvent, GridSizeChangedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { generateFileSummaryReport } from 'api/pdf/pdf-api';
import formatISO from 'date-fns/formatISO';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import _ from 'lodash';
import _debounce from 'lodash/debounce';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import PDFIcon from 'ui-kit/icons/PDF';
import { syncNoRowsOverlay } from 'utilities/ag-grid/grid-utility';
import { openDocument } from 'utilities/utilities';
import { getFileDetail, searchFiles } from '../../../../api/file/file-api';
import useAsync from '../../../../hooks/useAsync';
import LoadingSpinner from '../../../../ui-kit/components/LoadingSpinner';
import { Notification } from '../../../../ui-kit/components/notification/Notification';
import { DocumentType, fileStatus, httpErrorCodes, KBLinks, newWindow, WindowFeatures } from '../../../constants';
import SearchField from '../search-form/SearchField';
import { FileResults, searchOptionsType } from '../types';
import './FilesList.scss';
import {
  FormatDate as CreateDateRenderer,
  FileNumber as FileNumberRenderer,
  getDateRange,
  getFileListColDefs,
  getTextFilters,
  StatusCellRenderer,
} from './filesListConfigs';
import FloatingFilterComponent from './FloatingFilterComponent';

const TEXT_KEYS = ['fileNumber', 'address', 'buyer', 'searchText'] as const;
type TextKey = (typeof TEXT_KEYS)[number];

type PreferredOfficeEntry = {
  FirmID: number;
  IsSelected: number;
  FirmLocationJurisdictionID: number;
};

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  paper: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(3),
    paddingTop: 0,
    paddingBottom: 0,
  },
  searchWrap: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableStyles: {
    marginTop: '0px !important',
    height: 'calc(100vh - 287px) !important',
    width: '100% !important',
  },
  noResultsType: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const FilesList: React.FC = () => {
  const { getAccessToken } = useAuth();
  const classes = useStyles();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, preferredOffice } = profileCtx;
  const history = useHistory();

  const preferredAccountIds = useMemo(() => {
    if (!preferredOffice?.length || !userFirm?.firmId) {
      return null;
    }

    const firmIdStr = String(userFirm.firmId);
    const accountIds = preferredOffice.reduce((acc: number[], entry: PreferredOfficeEntry) => {
      if (String(entry.FirmID) === firmIdStr && entry.IsSelected === 0) {
        acc.push(entry.FirmLocationJurisdictionID);
      }
      return acc;
    }, []);

    return accountIds.length > 0 ? accountIds : null;
  }, [preferredOffice, userFirm?.firmId]);

  const defaultSearchPayLoad: searchOptionsType = useMemo(
    () => ({
      firmId: userFirm?.firmId ?? null,
      pageSize: 500,
      searchText: '',
      status: fileStatus[0].value,
      openDateFrom: formatISO(new Date().setFullYear(new Date().getFullYear() - 10)),
      openDateTo: formatISO(new Date()),
      accountIds: preferredAccountIds,
    }),
    [userFirm?.firmId, preferredAccountIds],
  );

  const [searchPayload, setSearchPayload] = useState<searchOptionsType>(defaultSearchPayLoad);
  useEffect(() => {
    setSearchPayload(defaultSearchPayLoad);
  }, [defaultSearchPayLoad]);

  const [searchText, setSearchText] = useState('');
  const [displayError, setDisplayError] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [selectedFileId, setSelectedFileId] = useState('');
  const [displayedRowCount, setDisplayedRowCount] = useState(0);

  const gridRef = useRef<AgGridReact>(null);
  const secondaryFilterOptions = useRef<Record<string, unknown>>({ searchText: '', state: '' });
  const prevFilterModelRef = useRef<Record<string, unknown>>({});
  const prevTextFiltersRef = useRef<Record<TextKey, string>>({
    fileNumber: '',
    address: '',
    buyer: '',
    searchText: '',
  });
  const flipStatusToAllNextRef = useRef(false);

  const { addGlobalMsg } = useGlobalMessages();
  const { addSnackbarMessage } = useSnackBars();
  const isFraudValidationDisabled = false;

  useEffect(() => {
    if (!userFirm?.firmId) history.push('/err');
  }, [userFirm, history]);

  const search = useCallback(async (): Promise<{ result: FileResults; errorCode?: string | number }> => {
    const token = await getAccessToken();
    return searchFiles(searchPayload, token);
  }, [getAccessToken, searchPayload]);

  const {
    execute,
    status,
    value: fileResults,
  } = useAsync<{ result: FileResults; errorCode?: string | number; message?: string }>(search, false);

  const getFilePdf = useCallback(async (): Promise<unknown> => {
    const token = await getAccessToken();
    return getFileDetail(selectedFileId ?? '', token);
  }, [getAccessToken, selectedFileId]);

  const { execute: executeFilePdf, value: pdfValue, status: pdfStatus } = useAsync<any>(getFilePdf, false);

  useEffect(() => {
    if (pdfStatus === 'success' && pdfValue?.pdfData) {
      openDocument(pdfValue.pdfData, DocumentType, newWindow, WindowFeatures);
    }
  }, [pdfStatus, pdfValue]);

  const filterOnSearch = useMemo(
    () =>
      _debounce((value: string) => {
        if (value?.trim().length >= 3) {
          setSearchPayload((prev) => ({ ...prev, searchText: value.trim() }));
        } else if (prevTextFiltersRef.current.searchText.length >= 3 && value.trim().length === 0) {
          setSearchPayload((prev) => ({ ...prev, searchText: '' }));
        }
      }, 250),
    [],
  );

  useEffect(() => {
    return () => {
      filterOnSearch.cancel();
    };
  }, [filterOnSearch]);

  useEffect(() => {
    if (selectedFileId) {
      executeFilePdf();
      const t = setTimeout(() => setSelectedFileId(''), 3000);
      return () => clearTimeout(t);
    }
  }, [selectedFileId, executeFilePdf]);

  useEffect(() => {
    execute().then(() => {
      try {
        gridRef.current?.api && syncNoRowsOverlay(gridRef.current.api);
      } catch (error) {
        console.warn('AgGrid failed to sync overlay after execute:', error);
      }
    });
  }, [searchPayload, execute]);

  useEffect(() => {
    if (!gridRef.current?.api) return;
    const api = gridRef.current.api;

    if (fileResults?.result) {
      setDisplayError(false);
      const rowData = fileResults.result.files ?? [];
      api.setGridOption(
        'rowData',
        rowData.map((data) => ({
          ...data,
          summary: data.summary ?? '',
          openDate: data.openDate ?? null,
          modifiedDate: data.modifiedDate ?? null,
        })),
      );
      syncNoRowsOverlay(api);
    } else if (fileResults?.errorCode) {
      api.hideOverlay();
      if (
        fileResults.errorCode == httpErrorCodes.notFound ||
        fileResults.errorCode == httpErrorCodes.badRequest ||
        fileResults.errorCode == httpErrorCodes.forbidden ||
        fileResults.errorCode == httpErrorCodes.internalServerError
      ) {
        setErrMsg(fileResults?.message ?? 'Error. Failed to GET.');
        setDisplayError(true);
      }
    }
  }, [fileResults]);

  const resetGridInputFocus = useCallback(() => {
    const focusElement = document.querySelectorAll('.ag-header-cell.ag-floating-filter.ag-focus-managed');
    focusElement.forEach((item) => item.removeAttribute('tabindex'));
  }, []);

  const hideOverlay = useCallback(() => {
    if (gridRef.current?.api) syncNoRowsOverlay(gridRef.current.api);
    resetGridInputFocus();
  }, [resetGridInputFocus]);

  const savedFilterModel = useCallback(
    (event: FilterChangedEvent) => {
      if (!gridRef.current) return;
      const { api } = gridRef.current;
      const curr = api.getFilterModel() ?? {};

      const prev = prevFilterModelRef.current;
      const currNoStatus = _.omit(curr, 'status');
      const prevNoStatus = _.omit(prev, 'status');

      const nonStatusChanged = !_.isEqual(currNoStatus, prevNoStatus);
      const statusOnlyChanged = _.isEqual(currNoStatus, prevNoStatus) && !_.isEqual(curr['status'], prev['status']);

      if (statusOnlyChanged) {
        prevFilterModelRef.current = _.cloneDeep(curr);
        return;
      }

      if (!flipStatusToAllNextRef.current && nonStatusChanged && curr['status']) {
        flipStatusToAllNextRef.current = true;
        const next = { ...curr };
        delete (next as Record<string, unknown>)['status'];
        api.setFilterModel(next);
        api.onFilterChanged();
        flipStatusToAllNextRef.current = false;
        prevFilterModelRef.current = _.cloneDeep(next);
        return;
      }

      const filterModel = curr as Record<string, any>;
      const payload: searchOptionsType = {
        ...defaultSearchPayLoad,
        searchText: String(secondaryFilterOptions.current.searchText ?? ''),
      };

      if (filterModel.status) payload.status = filterModel.status.type;
      if (filterModel.fileNumber) payload.fileNumber = filterModel.fileNumber.filter;
      if (filterModel.address) payload.address = filterModel.address.filter;
      if (filterModel.buyer) payload.buyer = filterModel.buyer.filter;
      if (filterModel.lender) payload.lender = filterModel.lender.filter;
      if (filterModel.seller) payload.seller = filterModel.seller.filter;

      if (filterModel.modifiedDate) {
        const dateRange = getDateRange(
          filterModel.modifiedDate.type,
          filterModel.modifiedDate.dateFrom,
          filterModel.modifiedDate.dateTo,
        );
        payload.modifiedDateFrom = dateRange.dateFromFormatISO;
        payload.modifiedDateTo = dateRange.dateToFormatISO;
        if (filterModel.modifiedDate.type !== 'inRange') {
          filterModel.modifiedDate.dateTo = filterModel.modifiedDate.dateTo ?? filterModel.modifiedDate.dateFrom;
        }
      }

      const currText = getTextFilters(filterModel, String(secondaryFilterOptions.current.searchText ?? ''));
      const prevText = prevTextFiltersRef.current;

      let shouldTrigger = false;
      for (const k of TEXT_KEYS) {
        const prevVal = (prevText[k] || '').trim();
        const currVal = (currText[k] || '').trim();
        const prevLen = prevVal.length;
        const currLen = currVal.length;

        const inZone = currLen >= 3 && (prevLen < 3 || currVal !== prevVal);
        const clearedToZero = prevLen > 0 && currLen === 0;

        if (inZone || clearedToZero) {
          shouldTrigger = true;
          break;
        }
      }

      if (shouldTrigger) {
        const searchKeys = Object.keys(payload);
        const defaultKeys = Object.keys(defaultSearchPayLoad);
        const hasNewKeys = searchKeys.some((key) => !defaultKeys.includes(key));
        if (hasNewKeys) {
          delete payload.openDateFrom;
          delete payload.openDateTo;
        }
        setSearchPayload(payload);
      }

      prevTextFiltersRef.current = currText;
      prevFilterModelRef.current = _.cloneDeep(filterModel);
      hideOverlay();

      queueMicrotask(() => {
        if (gridRef.current?.api) setDisplayedRowCount(gridRef.current.api.getDisplayedRowCount());
      });
    },
    [defaultSearchPayLoad, hideOverlay],
  );

  const onModelUpdated = useCallback(() => {
    if (gridRef.current?.api) setDisplayedRowCount(gridRef.current.api.getDisplayedRowCount());
  }, []);

  const CustomOverlayLoading = useCallback(() => {
    return (
      <div className="no-rows">
        <LoadingSpinner status="pending" className="files-container--spinner-root" />
      </div>
    );
  }, []);

  const downloadReportPdf = useCallback(
    async (fileId: string, fileNumber: string) => {
      addSnackbarMessage({ message: `Generating File Summary Report...`, type: 'success' });
      const token = await getAccessToken();
      await generateFileSummaryReport(
        fileId,
        fileNumber,
        isFraudValidationDisabled ? '' : userFirm?.firmId ?? '',
        token,
        addGlobalMsg,
      );
    },
    [addGlobalMsg, addSnackbarMessage, getAccessToken, isFraudValidationDisabled, userFirm?.firmId],
  );

  const debouncedSizeColumnsToFit = useMemo(
    () =>
      _debounce((api: any) => {
        api.sizeColumnsToFit();
      }, 100),
    [],
  );

  const onGridSizeChanged = useCallback(
    (params: GridSizeChangedEvent) => {
      debouncedSizeColumnsToFit(params.api);
    },
    [debouncedSizeColumnsToFit],
  );

  const colDefs: ColDef[] = useMemo(() => getFileListColDefs(), []);

  const onFirstDataRendered = useCallback((params: FirstDataRenderedEvent) => {
    params.api.setFilterModel?.({
      status: {
        values: ['Open'],
        filterType: 'set',
      },
      transactionType: {
        values: ['Sale/Cash', 'Sale w/Mortgage', 'Refinance', 'Equity Loan', 'Sale w/Construction Loan', 'Other', ''],
        filterType: 'set',
      },
    });
  }, []);

  const getDisplayedRowCountText = (): string => {
    const displayedCount = displayedRowCount ?? 0;
    const totalCount = fileResults?.result?.files?.length ?? 0;
    return `Files: ${displayedCount} of ${totalCount}`;
  };

  return (
    <ContentContainer fullWidth mTop={'0'}>
      <div>
        {displayError && (
          <Notification
            msg={errMsg}
            severity="error"
            className="files-container--alert"
            action={
              <Button color="inherit" variant="text" endIcon={<CloseIcon />} onClick={() => setDisplayError(false)}>
                ACTION
              </Button>
            }
          />
        )}
        <PageHeader
          title="All Files"
          contentRight={
            <div className={classes.searchWrap}>
              <SearchField
                onChange={(e) => {
                  const val = e.target.value ?? '';
                  setSearchText(val);
                  secondaryFilterOptions.current.searchText = val;
                  filterOnSearch(val);
                }}
                value={searchText}
                qaAttribute="FileListMyFile"
                onSearch={filterOnSearch}
                placeholder="Filter any column..."
                compact
              />
            </div>
          }
          subtitle="This page shows all your files. You can sort and filter the grid by any column."
          divider={false}
          menuItems={[{ label: 'Knowledge Base', link: KBLinks.filesList }]}
        />
        <Grid className={classes.paper}>
          {status === 'error' ? (
            <h3>Error in retrieving files</h3>
          ) : (
            <div className="grid-summary">
              <div className="files-list-grid">
                <div className={`ag-theme-alpine table-grid ${classes.tableStyles}`}>
                  <DataTable
                    loading={fileResults?.result === undefined}
                    ref={gridRef}
                    columnDefs={colDefs}
                    onGridSizeChanged={onGridSizeChanged}
                    components={{
                      statusCellRenderer: StatusCellRenderer,
                      fileNumberRenderer: FileNumberRenderer,
                      createDateRenderer: CreateDateRenderer,
                      summaryRenderer: ({ id, fileNumber }: { id: string; fileNumber: string }) => (
                        <PDFIcon
                          fontSize="small"
                          onClick={() => {
                            downloadReportPdf(id, fileNumber);
                          }}
                        />
                      ),
                      customOverlayLoading: CustomOverlayLoading,
                      customFloatingFilter: FloatingFilterComponent,
                    }}
                    loadingOverlayComponent={'customOverlayLoading'}
                    defaultColDef={{
                      cellClass: 'left-align-column',
                      headerClass: 'left-align-header',
                      sortable: true,
                      filter: 'agTextColumnFilter',
                      floatingFilter: true,
                      filterParams: { closeOnApply: true, suppressAndOrCondition: true },
                      suppressMenu: true,
                      width: 180,
                      minWidth: 180,
                      resizable: true,
                      suppressSizeToFit: false,
                      flex: 1,
                    }}
                    onFilterChanged={savedFilterModel}
                    onModelUpdated={onModelUpdated}
                    onFirstDataRendered={onFirstDataRendered}
                    gridOptions={{
                      suppressRowClickSelection: true,
                      icons: { filter: '<span class="ag-icon ag-icon-filter-custom"></span>' },
                    }}
                  />
                </div>
                <Box py={2}>
                  <Typography color="textPrimary" variant="body2">
                    {getDisplayedRowCountText()}
                  </Typography>
                </Box>
              </div>
            </div>
          )}
        </Grid>
      </div>
    </ContentContainer>
  );
};

export default FilesList;
